/**
 * Tradução da forma crua da API (inglês) para o domínio pt-BR da tela de
 * revisão (`TP-050`, T9/T10). Roda UMA vez, logo depois do fetch — mesma
 * disciplina de `features/contratos/api/mapeadores.ts`.
 */

import { mapearItem, TIPO_DE_FONTE } from '../../api/mapeadores';
import type { ItemApi } from '../../api/tipos';
import type {
  AprovacaoEmLoteApi,
  ConfiancaDaRegra,
  ConfiancaDaRegraApi,
  DiffDaFonte,
  EstadoDaRegra,
  EstadoDaRegraApi,
  ItemDoDiff,
  LadoDoItemDoDiff,
  MotivoDoDiffDoItem,
  MotivoDoDiffDoItemApi,
  PaginaDaFonte,
  PaginaDaFonteApi,
  RegraAjustadaNoDiff,
  RegraDaFila,
  RegraDaFilaApi,
  RegraRevisada,
  ReferenciaDeFonte,
  ReferenciaDeFonteApi,
  RegraRevisadaApi,
  SourceDiffApi,
  TipoDeRegra,
  TipoDeRegraApi,
} from './tipos';

export const TIPO_DE_REGRA: Record<TipoDeRegraApi, TipoDeRegra> = {
  BillingUnit: 'unidade_cobranca',
  Inclusion: 'inclusao',
  Exclusion: 'exclusao',
  PriceFormula: 'formula_preco',
  Authorization: 'autorizacao',
  BillingRule: 'regra_cobranca',
  DocumentaryDeadline: 'prazo_documental',
  Sla: 'sla',
  GlosaHypothesis: 'hipotese_glosa',
  AppealDeadline: 'prazo_recurso',
  NotClassifiable: 'nao_classificavel',
};

/** Inverso de `TIPO_DE_REGRA` — o envio (lote, por exemplo) manda o NOME do membro em inglês. */
export const TIPO_DE_REGRA_PARA_API: Record<TipoDeRegra, TipoDeRegraApi> = {
  unidade_cobranca: 'BillingUnit',
  inclusao: 'Inclusion',
  exclusao: 'Exclusion',
  formula_preco: 'PriceFormula',
  autorizacao: 'Authorization',
  regra_cobranca: 'BillingRule',
  prazo_documental: 'DocumentaryDeadline',
  sla: 'Sla',
  hipotese_glosa: 'GlosaHypothesis',
  prazo_recurso: 'AppealDeadline',
  nao_classificavel: 'NotClassifiable',
};

export const CONFIANCA: Record<ConfiancaDaRegraApi, ConfiancaDaRegra> = {
  High: 'alta',
  Medium: 'media',
  Low: 'baixa',
};

const ESTADO_DA_REGRA: Record<EstadoDaRegraApi, EstadoDaRegra> = {
  Extracted: 'extraida',
  Approved: 'aprovada',
  Adjusted: 'ajustada',
  Rejected: 'rejeitada',
  Retired: 'aposentada',
};

const MOTIVO_DO_DIFF: Record<MotivoDoDiffDoItemApi, MotivoDoDiffDoItem> = {
  Added: 'entrou',
  Removed: 'saiu',
  Changed: 'mudou',
};

export function mapearRegraDaFila(bruto: RegraDaFilaApi): RegraDaFila {
  return {
    id: bruto.id,
    fonteId: bruto.sourceId,
    tipoDeFonte: bruto.sourceTypeToken,
    tipo: TIPO_DE_REGRA[bruto.type],
    confianca: CONFIANCA[bruto.confidence],
    escopo: bruto.scope,
    efeito: bruto.effect,
    trecho: bruto.excerpt,
    pagina: bruto.page,
    trechoLocalizado: bruto.excerptFound,
    observacoes: bruto.notes,
    descricoesDosItens: bruto.itemDescriptions ?? {},
  };
}

export function mapearRegraRevisada(bruto: RegraRevisadaApi): RegraRevisada {
  return { estado: ESTADO_DA_REGRA[bruto.status], novaRegraId: bruto.novaRegraId };
}

export function mapearAprovacaoEmLote(bruto: AprovacaoEmLoteApi): number {
  return bruto.aprovadas;
}

function mapearReferenciaDeFonte(bruto: ReferenciaDeFonteApi | null | undefined): ReferenciaDeFonte | null {
  if (bruto === null || bruto === undefined) return null;
  return {
    id: bruto.id,
    tipo: TIPO_DE_FONTE[bruto.type],
    criadaEm: bruto.createdAt,
    vigenciaIni: bruto.validFrom,
    paginas: bruto.pages,
    totalDeItens: bruto.itemCount,
    hash: bruto.hashPrefix,
  };
}

export function mapearDiffDaFonte(bruto: SourceDiffApi): DiffDaFonte {
  const ladoDoItem = (lado: ItemApi | null): LadoDoItemDoDiff | null => {
    if (lado === null) return null;
    const { id, descricao, unidadeDeCobranca, valor, pagina } = mapearItem(lado);
    return { id, descricao, unidadeDeCobranca, valor, pagina };
  };
  const itens: ItemDoDiff[] = (bruto.items ?? []).map((item) => ({
    codigo: item.code,
    antes: ladoDoItem(item.before),
    depois: ladoDoItem(item.after),
    motivo: MOTIVO_DO_DIFF[item.reason],
  }));

  const regrasAjustadas: RegraAjustadaNoDiff[] = (bruto.adjustedRules ?? []).map((regra) => ({
    regraId: regra.ruleId,
    tipo: TIPO_DE_REGRA[regra.type],
    substituiRegraId: regra.replacesRuleId,
  }));

  return {
    primeiraFonte: bruto.isFirstSource,
    atual: mapearReferenciaDeFonte(bruto.current),
    anterior: mapearReferenciaDeFonte(bruto.previous),
    itens,
    regrasAjustadas,
  };
}

export function mapearPaginaDaFonte(bruto: PaginaDaFonteApi): PaginaDaFonte {
  return { pagina: bruto.pagina, texto: bruto.texto, mascarado: bruto.mascarado, observacao: bruto.observacao };
}
